import { useEffect, useMemo, useRef } from 'react';
import { createContext } from '@shared/utils/createContext';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { DraftData } from './CardCustomizer.types';
import type { DraftJson } from './DraftJson.types';
import { useUpdateStationeryDraftMutation } from './CardCustomizer.graphql.generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { throttle } from 'lodash-es';

type DraftSaverContextValue = Readonly<{
  /** The draft this customizer is editing, paper or digital. */
  draftId: string;
  cancelPendingSave: () => void;
  flushPendingSave: () => Promise<void>;
}>;

const [DraftSaverProvider, useDraftSaverContext] = createContext<DraftSaverContextValue>({
  name: 'DraftSaver',
});

const useDraftSaverCancel = () => useDraftSaverContext().cancelPendingSave;
const useDraftSaverFlush = () => useDraftSaverContext().flushPendingSave;
/** The draft being edited, in either customizer. */
const useCustomizerDraftId = () => useDraftSaverContext().draftId;

export { DraftSaverProvider, useDraftSaverCancel, useDraftSaverFlush, useCustomizerDraftId };

export const useDraftSaver = ({ draft, draftId, disable }: { draft: DraftData; draftId: string; disable?: boolean }) => {
  const data = useEventInfo();
  // Initializing ref value to null instead of the `draft.config`
  // so that we can force an update on the first mount.
  //
  // This is particularly important when going from public shop to admin
  // and we need to associate the draft to an event.
  const draftRef = useRef<DraftJson | null>(null);
  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const lastWrittenConfigRef = useRef<DraftJson | null>(null);
  const eventId = data.eventInfo?.eventId;
  const [update, { loading }] = useUpdateStationeryDraftMutation();

  const updateDraft = useEventCallback(() => {
    if (!eventId) {
      return Promise.resolve();
    }

    const config = draft.config;
    const promise = Promise.resolve(
      update({
        variables: {
          payload: {
            id: draftId,
            eventId,
            cardJSON: config,
          },
        },
      }),
    ).then(result => {
      lastWrittenConfigRef.current = config;
      return result;
    });

    inFlightRef.current = promise;
    const settled = promise.finally(() => {
      if (inFlightRef.current === promise) {
        inFlightRef.current = null;
      }
    });
    // The throttled auto-save discards this promise, so a failed background save would surface as
    // an unhandled rejection. `flushPendingSave` awaits it and still receives the rejection.
    settled.catch(() => {});
    return settled;
  });

  // Memoize the throttled call
  const throttledUpdate = useMemo(() => {
    // Throttle the call to exectue once every 2 seconds
    return throttle(updateDraft, 2000);
  }, [updateDraft]);

  useEffect(() => {
    if (disable) {
      return;
    }

    // If draft prop is different than the locally stored draft, update the draft
    if (draftRef.current !== draft.config) {
      // Don't execute if an update is already underway
      if (loading) {
        return;
      }

      // Update the local draft ref
      draftRef.current = draft.config;
      throttledUpdate();
    }
  }, [draft.config, throttledUpdate, loading, disable]);

  const cancelPendingSave = useEventCallback(() => {
    throttledUpdate.cancel();
  });

  /**
   * Write the in-memory draft now and wait until it has landed.
   *
   * Checkout and add-to-cart read `draft.cardJSON` on the server. Quantity (and
   * everything else) only lives in immer until this saver writes it, and the
   * 2s throttle plus a `_self` Stripe redirect will drop a trailing save.
   * Flushing first is required whether or not checkout is server-authoritative:
   * authority only changes *which* qty is charged, not whether the screen and
   * the draft agree.
   *
   * Cancels the throttle rather than `flush()`ing it: the leading invoke already
   * writes on the first edit, and a lodash trailing flush would send that same
   * payload a second time.
   */
  const flushPendingSave = useEventCallback(async () => {
    if (disable) {
      return;
    }

    throttledUpdate.cancel();
    // A failed in-flight save rejects the flush, even though the write below would send the
    // latest config anyway. The customer's retry click succeeds, since the failed save has
    // cleared by then. If flaky connections turn out to be common here, swallow this rejection
    // (`.catch(() => {})`) so the fresh write decides the outcome.
    if (inFlightRef.current) {
      await inFlightRef.current;
    }

    if (eventId && lastWrittenConfigRef.current !== draft.config) {
      // Mark this config as seen, or the auto-save effect re-sends it once `loading` settles.
      draftRef.current = draft.config;
      await updateDraft();
    }
  });

  return useMemo(() => ({ draftId, cancelPendingSave, flushPendingSave }), [draftId, cancelPendingSave, flushPendingSave]);
};
